import { useI18n } from '@/lib/i18n';
import Footer from '@/components/Footer';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AboutPage = () => {
  const { lang, t } = useI18n();

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const title = lang === 'ru'
    ? ((settings as any)?.about_page_title_ru || settings?.about_title_ru)
    : ((settings as any)?.about_page_title_en || settings?.about_title_en);
  const text = lang === 'ru'
    ? ((settings as any)?.about_page_text_ru || settings?.about_text_ru)
    : ((settings as any)?.about_page_text_en || settings?.about_text_en);

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Hero image */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        {settings?.about_image_url ? (
          <img
            src={settings.about_image_url}
            alt="About"
            className="w-full h-full object-cover object-[center_35%]"
          />
        ) : (
          <div className="w-full h-full bg-secondary flex items-center justify-center">
            <span className="text-6xl font-display font-bold text-muted-foreground/20">✦</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40" />

        {/* Back button */}
        <Link
          to="/"
          className="absolute top-6 left-6 z-10 flex items-center gap-2 text-[#f3f6f7] hover:text-[#f3f6f7]/60 transition-colors text-sm uppercase tracking-widest"
        >
          <ArrowLeft size={18} />
          {t('Назад', 'Back')}
        </Link>

        {/* Title over image */}
        <div className="absolute bottom-0 left-0 right-0 container pb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-display font-bold text-[#f3f6f7] leading-tight"
          >
            {title || t('Обо мне', 'About me')}
          </motion.h1>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="container py-16 md:py-24"
      >
        <div className="max-w-3xl space-y-8">
          <div className="space-y-6 text-lg md:text-xl leading-relaxed text-muted-foreground">
            {text ? (
              text.split('\n').map((p, i) => <p key={i}>{p}</p>)
            ) : (
              <>
                <p>{t('Я креативный арт-директор, специализируюсь на шоу и рекламных кампаниях. Работаю на стыке идеи, визуального языка и технологии, помогая превращать задачу в целостное креативное решение.', 'I am a creative art director specializing in shows and advertising campaigns. I work at the intersection of idea, visual language, and technology, helping transform briefs into cohesive creative solutions.')}</p>
                <p>{t('Мой основной блок работы — препродакшн. Я прекрасно понимаю насколько это сложный, напряжённый и ответственный момент как для агентства с продакшном, так и для клиента.', 'My core area of work is pre-production. I understand perfectly how complex, intense, and critical this stage is for both the agency with production and the client.')}</p>
                <p>{t('Моя задача сделать этот этап как можно более комфортным.', 'My goal is to make this stage as smooth as possible.')}</p>
                <p>{t('В своей работе я исхожу из того, что визуал — это не оформление, а инструмент коммуникации. Он должен быть точным, осмысленным и работать на задачу проекта: усиливать идею, удерживать внимание и формировать нужное восприятие у зрителя.', 'In my work, I believe that visuals are not decoration but a communication tool. They must be precise, intentional, and serve the project\'s goals: reinforcing the idea, holding attention, and shaping the right perception for the viewer.')}</p>
                <p>{t('Я сопровождаю проекты на всех ключевых этапах: от разработки концепции и визуальной стратегии до художественного контроля реализации. Умею работать в сложных продакшн-процессах, выстраивать взаимодействие между командами и сохранять качество и целостность идеи на каждом этапе.', 'I support projects at every key stage: from concept development and visual strategy to artistic oversight of execution. I know how to navigate complex production processes, build collaboration between teams, and maintain quality and integrity of the idea at every step.')}</p>
                <p>{t('За более чем 12 лет работы я участвовал в самых крупных рекламных и шоу-проектах, сотрудничал с агентствами, продюсерами и брендами, работал с командами разного масштаба. Мой опыт включает проекты, отмеченные профессиональными наградами, а также задачи с максимально высоким уровнем ответственности и публичности.', 'Over 12+ years of work, I have been involved in the largest advertising and show projects, collaborating with agencies, producers, and brands, working with teams of various scales. My experience includes award-winning projects as well as tasks with the highest level of responsibility and public visibility.')}</p>
                <p>{t('Визуальный язык, с которым я работаю, строится на ясной идее, продуманной композиции и внимании к контексту.', 'The visual language I work with is built on a clear idea, thoughtful composition, and attention to context.')}</p>
                <p>{t('Мне близка работа в проектах, где важны профессиональный подход, доверие и понимание целей. Я ценю ясные задачи, сильные команды и ситуации, в которых креатив становится результатом совместной, осмысленной работы.', 'I thrive in projects where professionalism, trust, and understanding of goals matter. I value clear objectives, strong teams, and situations where creativity becomes the result of collaborative, purposeful work.')}</p>
              </>
            )}
          </div>
        </div>
      </motion.div>
      <Footer />
    </main>
  );
};

export default AboutPage;
